import { forwardRef } from 'react';
import { HStack, Text } from '@chakra-ui/react';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { RiftboundButton, type RiftboundButtonProps } from './button';

export type RiftboundMenuTriggerChevronAlign = 'inline' | 'end';

export interface RiftboundMenuTriggerButtonProps extends Omit<RiftboundButtonProps, 'children'> {
  label: string;
  icon?: React.ReactNode;
  /** When true, the trigger is open: same field fill, with the accent edge. */
  active: boolean;
  /** Optional secondary text, commonly used for the selected value. */
  summary?: string;
  /** Hide label/summary at smaller breakpoints when the icon is enough. */
  responsiveLabel?: boolean;
  chevronExpanded?: boolean;
  chevronAlign?: RiftboundMenuTriggerChevronAlign;
}

/**
 * Riftbound-styled trigger for compact popover/menu controls.
 * Pair with `RiftboundPopover` for dropdown filters, sort menus, and toolbar actions.
 */
export const RiftboundMenuTriggerButton = forwardRef<
  HTMLButtonElement,
  RiftboundMenuTriggerButtonProps
>(function RiftboundMenuTriggerButton(
  {
    label,
    icon,
    active,
    summary,
    responsiveLabel = false,
    chevronExpanded = false,
    chevronAlign = 'inline',
    size = 'sm',
    variant,
    colorPalette,
    ...rest
  },
  ref,
) {
  const mergedVariant = variant ?? 'outline';
  const mergedPalette = colorPalette;

  const labelEl = (
    <Text
      as="span"
      fontSize="sm"
      truncate
      display={responsiveLabel ? { base: 'none', md: 'inline' } : undefined}
    >
      {label}
    </Text>
  );

  const summaryEl =
    active && summary ? (
      <Text
        as="span"
        fontSize="xs"
        opacity={0.7}
        maxW="80px"
        truncate
        display={responsiveLabel ? { base: 'none', lg: 'inline' } : undefined}
      >
        · {summary}
      </Text>
    ) : null;

  const chevron = (
    <MdKeyboardArrowDown
      size={14}
      style={{
        opacity: 0.6,
        flexShrink: 0,
        transform: chevronExpanded ? 'rotate(-180deg)' : undefined,
        transition: 'transform 0.15s ease',
      }}
    />
  );

  return (
    <RiftboundButton
      ref={ref}
      type="button"
      size={size}
      variant={mergedVariant}
      colorPalette={mergedPalette}
      borderColor={active ? 'accent' : undefined}
      flexShrink={0}
      px={2.5}
      fontWeight="normal"
      justifyContent={chevronAlign === 'end' ? 'space-between' : 'flex-start'}
      gap={chevronAlign === 'end' ? 2 : 1.5}
      {...rest}
    >
      {chevronAlign === 'end' ? (
        <>
          <HStack gap={1.5} minW={0} flex={1} alignItems="center">
            {icon}
            {labelEl}
            {summaryEl}
          </HStack>
          {chevron}
        </>
      ) : (
        <>
          {icon}
          {labelEl}
          {summaryEl}
          {chevron}
        </>
      )}
    </RiftboundButton>
  );
});
